import { useState } from "react";
import InputField from "components/fields/InputField";
import Checkbox from "components/checkbox";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/authContext";
import authImg from "assets/img/auth/auth2.jpg";
import { pupilLogin, userLogin } from "api";

function SignInDefault() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState("signin"); // "signin" or "joinclassroom"
  
  const navigate = useNavigate();
  const { login } = useAuth();

  // Clear errors when input changes
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (errors.general) {
      setErrors({});
      // Add a subtle visual feedback that the error was cleared
      const emailField = document.getElementById('email');
      if (emailField) {
        emailField.classList.add('border-green-500');
        setTimeout(() => {
          emailField.classList.remove('border-green-500');
        }, 1000);
      }
    }
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (errors.general) {
      setErrors({});
      // Add a subtle visual feedback that the error was cleared
      const passwordField = document.getElementById('password');
      if (passwordField) {
        passwordField.classList.add('border-green-500');
        setTimeout(() => {
          passwordField.classList.remove('border-green-500');
        }, 1000);
      }
    }
  };

  // Clear errors when switching tabs
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setErrors({});
    setEmail("");
    setPassword("");
    setIsLoading(false);
    setShowPassword(false);
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    try {
      const response = await userLogin({
        email,
        password,
        rememberMe
      });

      if (response.token) {
        // Use the existing login function with the token
        await login(response.token, rememberMe);
        // Navigate based on user role
        if (response.user.role === 'mentor') {
          navigate("/admin/mentor-hub");
        } else {
          navigate("/admin/dashboard");
        }
      } else {
        setErrors({ general: "Invalid response from server" });
      }
    } catch (error) {
      if (error.response?.data?.msg) {
        setErrors({ general: error.response.data.msg });
      } else {
        setErrors({ general: error.message || "Failed to sign in. Please try again." });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinClassroom = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    // Basic validation - just check if fields are not empty
    if (!email.trim() || !password.trim()) {
      setErrors({ general: "Please enter both username and classroom code" });
      setIsLoading(false);
      return;
    }

    const submissionData = {
      username: email,
      classroomCode: password
    };

    console.log("Submitting classroom login with:", submissionData);

    try {
      const response = await pupilLogin(submissionData);

      if (response.token) {
        // Use the existing login function with the token
        await login(response.token, rememberMe);
        // Navigate to appropriate page for pupils
        navigate("/admin/dashboard");
      } else {
        setErrors({ general: "Invalid response from server" });
      }
    } catch (error) {
      console.error("Classroom login error:", error);
      if (error.response?.data?.msg) {
        setErrors({ general: error.response.data.msg });
      } else {
        setErrors({ general: error.message || "Failed to join classroom. Please try again." });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderForm = () => {
    if (activeTab === "signin") {
      return (
        <form onSubmit={handleSignIn} className="space-y-4">
          {errors.general && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg transition-all duration-300 ease-in-out">
              <p className="text-red-600 dark:text-red-400 text-sm">{errors.general}</p>
            </div>
          )}

          <InputField
            variant="auth"
            extra="mb-3"
            label="Email*"
            placeholder="mail@simmmple.com"
            id="email"
            type="email"
            value={email}
            onChange={handleEmailChange}
            required
          />

          <div className="relative">
            <InputField
              variant="auth"
              extra="mb-3"
              label="Password*"
              placeholder="Min. 8 characters"
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={handlePasswordChange}
              icon={showPassword ? <FaEye size={16} /> : <FaEyeSlash size={16} />}
              onIconClick={() => setShowPassword(!showPassword)}
              required
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Checkbox
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Remember me
              </label>
            </div>
            <a
              href="/auth/forgot-password"
              className="text-sm font-medium text-orange-500 hover:text-orange-600 dark:text-orange-400 dark:hover:text-orange-300"
            >
              Forgot password?
            </a>
          </div>

          <button
            className="w-full rounded-xl bg-orange-500 py-3 text-base font-medium text-white transition duration-200 hover:bg-orange-600 active:bg-orange-700 disabled:bg-gray-500 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
            type="submit"
            disabled={isLoading}
            aria-label="Sign In"
          >
            {isLoading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Signing In...
              </div>
            ) : (
              "Sign In"
            )}
          </button>

          <div className="text-center">
            <span className="text-sm font-medium text-navy-700 dark:text-gray-500">
              Not registered yet?
            </span>
            <a
              href="/auth/sign-up"
              className="ml-1 text-sm font-medium text-orange-500 hover:text-orange-600 dark:text-orange-400 dark:hover:text-orange-300"
            >
              Create an account
            </a>
          </div>
        </form>
      );
    } else {
      return (
        <form onSubmit={handleJoinClassroom} className="space-y-4">
          {errors.general && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg transition-all duration-300 ease-in-out">
              <p className="text-red-600 dark:text-red-400 text-sm">{errors.general}</p>
            </div>
          )}

          <InputField
            variant="auth"
            extra="mb-3"
            label="Username*"
            placeholder="Enter your username"
            id="username"
            type="text"
            value={email}
            onChange={handleEmailChange}
            required
          />

          <InputField
            variant="auth"
            extra="mb-3"
            label="Classroom Code*"
            placeholder="Enter your classroom code"
            id="classroomCode"
            type="text"
            value={password}
            onChange={handlePasswordChange}
            required
          />
          
          <div className="text-xs text-gray-500 dark:text-gray-400 text-center mb-4">
            <p>Enter the classroom code provided by your mentor</p>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Checkbox
                id="rememberMeClassroom"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <label htmlFor="rememberMeClassroom" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Remember me
              </label>
            </div>
          </div>

          <button
            className="w-full rounded-xl bg-orange-500 py-3 text-base font-medium text-white transition duration-200 hover:bg-orange-600 active:bg-orange-700 disabled:bg-gray-500 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
            type="submit"
            disabled={isLoading}
            aria-label="Join Classroom"
          >
            {isLoading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Joining...
              </div>
            ) : (
              "Join Classroom"
            )}
          </button>

          <div className="text-center text-sm text-gray-600 dark:text-gray-400">
            <p>Your mentor will create your account for you</p>
          </div>
        </form>
      );
    }
  };

  return (
    <div className="relative flex h-screen">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center px-8 lg:px-16 xl:px-24">
        <div className="w-full max-w-[400px]">
          {/* Tab Switcher */}
          <div className="mb-8">
            <div className="flex bg-gray-100 dark:bg-navy-700 rounded-lg p-1">
              <button
                onClick={() => handleTabChange("signin")}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                  activeTab === "signin"
                    ? "bg-white dark:bg-navy-600 text-orange-500 shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => handleTabChange("joinclassroom")}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                  activeTab === "joinclassroom"
                    ? "bg-white dark:bg-navy-600 text-orange-500 shadow-sm"
                    : "text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                }`}
              >
                Join Classroom
              </button>
            </div>
          </div>

          {/* Form Content */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-navy-700 dark:text-white mb-2">
              {activeTab === "signin" ? "Welcome Back" : "Join Your Classroom"}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {activeTab === "signin" 
                ? "Sign in to your account to continue" 
                : "Enter your username and classroom code to access your learning environment"
              }
            </p>
          </div>

          {renderForm()}
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 xl:w-2/5">
        <div className="h-full w-full bg-gradient-to-br from-orange-500 to-brand-900 lg:rounded-bl-[120px] xl:rounded-bl-[200px] overflow-hidden">
          <div
            style={{ backgroundImage: `url(${authImg})` }}
            className="h-full w-full bg-cover bg-center lg:rounded-bl-[120px] xl:rounded-bl-[200px]"
          />
        </div>
      </div>
    </div>
  );
}

export default SignInDefault;
